import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../../components/ui/accordion';
import { Button } from '../../../../components/ui/button';
import { Dialog, DialogTrigger } from '../../../../components/ui/dialog';
import { Badge } from '../../../../components/ui/badge';
import { PlusCircle, Edit } from 'lucide-react';
import { getAllAdminUsers } from '../../../../services/AdminApis'; // Adjust import path
import { usePermissions } from '../../../../hooks/usePermissions';
import { CreateUserDialog } from './CreateUserDialog';
import { EditUserDialog } from './EditUserDialog';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { hasPermission } = usePermissions();
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getAllAdminUsers();
      setUsers(data);
    } catch (error) {
      // Handle error (e.g., show a toast)
      console.error("Failed to fetch users", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreationSuccess = () => {
    setIsDialogOpen(false);
    fetchUsers(); // Refresh the list after a new user is created
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setIsEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    setIsEditDialogOpen(false);
    setSelectedUser(null);
    fetchUsers(); // Refresh the list to show the changes
  };

  const canEditUser = hasPermission('admin_user:manage');

  const activeUsers = users.filter((user) => user.is_active);
  const inactiveUsers = users.filter((user) => !user.is_active);

  const renderUserRow = (user) => (
    <TableRow key={user.id}>
      <TableCell>{[user.first_name, user.last_name].filter(Boolean).join(' ') || '—'}</TableCell>
      <TableCell>{user.email}</TableCell>
      <TableCell><Badge variant="secondary">{user.group || 'N/A'}</Badge></TableCell>
      <TableCell><Badge variant={user.is_active ? 'default' : 'destructive'}>{user.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
      {/* Add Actions cell if user can edit */}
      {canEditUser && (
        <TableCell className="text-right">
          <Button variant="ghost" size="icon" onClick={() => handleEditClick(user)}>
            <Edit className="h-4 w-4" />
          </Button>
        </TableCell>
      )}
    </TableRow>
  );

  return (
    <>
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Admin User Management</CardTitle>
        {/* Conditionally render the button based on permissions */}
        {hasPermission('admin_user:manage') && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <PlusCircle className="mr-2 h-4 w-4" /> Create User
              </Button>
            </DialogTrigger>
            <CreateUserDialog onSuccess={handleCreationSuccess} />
          </Dialog>
        )}
      </CardHeader>
      <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Group</TableHead>
                <TableHead>Status</TableHead>
                {/* Add Actions column header if user can edit */}
                {canEditUser && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeUsers.map(renderUserRow)}
            </TableBody>
          </Table>

          {inactiveUsers.length > 0 && (
            <Accordion type="single" collapsible className="mt-6">
              <AccordionItem value="inactive-admins">
                <AccordionTrigger>Inactive Admins ({inactiveUsers.length})</AccordionTrigger>
                <AccordionContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Group</TableHead>
                        <TableHead>Status</TableHead>
                        {canEditUser && <TableHead className="text-right">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inactiveUsers.map(renderUserRow)}
                    </TableBody>
                  </Table>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </CardContent>
      </Card>
    {/* Dialog for Editing a User */}
    {selectedUser && (
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        {/* key forces a fresh EditUserDialog instance (and fresh useState
            initial values) per user - without it, React reuses the same
            instance across edits since `selectedUser` alone doesn't change
            identity in a way that unmounts it, leaving stale/blank fields. */}
        <EditUserDialog
          key={selectedUser.id}
          user={selectedUser}
          onSuccess={handleEditSuccess}
          onClose={() => setIsEditDialogOpen(false)}
        />
      </Dialog>
    )}
    </>
  );
};

export default AdminUsers;